<template>
    <loading-indicator v-if="$store.state.pageLoading" key="loadingIndicator"/>
    <div v-else id="module-content-base" key="contentContainer">
        <div v-if="$slots.printHeader" class="visible-print">
            <slot name="printHeader"/>
        </div>

        <!-- force usage of current headline due to document title being set -->
        <div class="headline" v-if="(currentHeadline || true) && !customHeadline && (!isExportRequest || currentExportSettings.headline || $slots.navigation)">
            <content-breadcrumb v-if="breadcrumb.length && !isExportRequest" :entries="breadcrumb"/>
            <div class="headline-elements">
                <component v-if="!isExportRequest || currentExportSettings.headline" :is="isExportRequest ? 'h2' : 'h1'" class="text-overflow">
                    <span v-html="currentHeadline" v-tooltip="currentHeadline"></span>
                </component>

                <div class="headline-additional no-print">
                    <slot name="navigation"/>
                    <slot name="action-menu"/>
                </div>
            </div>
        </div>

        <slot name="headline"/>

        <div class="row">
            <div :class="contentClasses">
                <slot name="content-headline"/>

                <!-- flash messages -->
                <div class="status-messages" v-if="flashMessages.length">
                    <div v-for="entry in flashMessages" :class="entry.class">
                        <i class="material-icons md-light" aria-hidden="true" role="img">{{entry.icon}}</i>
                        <div v-html="entry.text"></div>
                    </div>
                </div>

                <!-- page content -->
                <slot/>
            </div>
            <div id="module-content-sidebar" class="no-print" :class="sidebarClasses" v-if="useSidebar">
                <slot name="sidebar"/>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {exportEnabled, exportSettings} from '@/library/export';
    import StripTags from '../library/form/filter/StripTags';
    import ContentBreadcrumb from './ContentBreadcrumb.vue';
    import LoadingIndicator from './LoadingIndicator.vue';
    import {useStore} from 'vuex';
    import {computed, reactive} from 'vue';

    let originalTitle   = '';
    const props         = withDefaults(
        defineProps<{
            headline?: string;
            sidebarSize?: number;
            sidebarBreakpoint?: 'sm' | 'md' | 'lg' | 'xl';
            hideWrappedSidebar?: boolean;
            breadcrumb?: Breadcrumb[];
            sidebarSpace?: boolean;
            showSidebar?: boolean;
            customHeadline?: boolean;
        }>(),
        {
            headline: '',
            sidebarSize: 3,
            sidebarBreakpoint: 'md',
            hideWrappedSidebar: false,
            breadcrumb: [],
            sidebarSpace: false,
            showSidebar: true,
            customHeadline: false,
        },
    );
    const slots         = defineSlots<{
        sidebar: unknown;
        printHeader: unknown;
        navigation: unknown;
        headline: unknown;
        'action-menu': unknown;
        default: unknown;
    }>();

    const store         = useStore();
    const flashMessages = computed<FlashMessageData[]>(() => store.state.flashMessages);
    const breakpoints   = reactive(['xs', 'sm', 'md', 'lg', 'xl', 'xxl']);

    if (!originalTitle)
    {
        originalTitle = document.title;
    }

    const contentSize = computed(() =>
    {
        return 12 - props.sidebarSize;
    });

    // consider using reactive method @see: PortunaBox.showAdditionally
    const useSidebar = computed(() =>
    {
        return props.showSidebar && !!(props.sidebarSpace || slots.sidebar && slots.sidebar().length);
    });

    const currentHeadline = computed(() =>
    {
        let headline = props.headline;
        if (
            exportEnabled() && exportSettings()?.own_headline
        )
        {
            headline = exportSettings().own_headline as string;
        }
        document.title = [
            headline ? new StripTags().filter(headline) : '',
            exportEnabled()
                ? originalTitle
                : store.state.layoutData.title,
        ]
        .filter((t) => t)
        .join(' - ');
        store.commit('updateHeadline', headline);
        return headline || '&nbsp;';
    });

    const contentClasses = computed(() =>
    {
        let classes = ['col-12'];
        if (useSidebar.value)
        {
            if ('sm' === props.sidebarBreakpoint)
            {
                classes.push('col-md-' + contentSize.value);
            }
            else if ('md' === props.sidebarBreakpoint)
            {
                classes.push('col-lg-' + contentSize.value);
            }
            else if ('lg' === props.sidebarBreakpoint)
            {
                classes.push('col-xl-' + contentSize.value);
            }
            else if ('xl' === props.sidebarBreakpoint)
            {
                classes.push('col-xxl-' + contentSize.value);
            }
        }
        return classes;
    });
    const sidebarClasses  = computed(() =>
    {
        let classes = ['col-12'];
        if ('sm' === props.sidebarBreakpoint)
        {
            classes.push('col-md-' + props.sidebarSize);
        }
        else if ('md' === props.sidebarBreakpoint)
        {
            classes.push('col-lg-' + props.sidebarSize);
        }
        else if ('lg' === props.sidebarBreakpoint)
        {
            classes.push('col-xl-' + props.sidebarSize);
        }
        else if ('xl' === props.sidebarBreakpoint)
        {
            classes.push('col-xxl-' + props.sidebarSize);
        }
        if (props.hideWrappedSidebar)
        {
            const breakpointIndex = breakpoints.indexOf(props.sidebarBreakpoint);
            breakpointIndex > 0 && classes.push(`hidden-${props.sidebarBreakpoint}-down`);
        }
        return classes;
    });
</script>